// app/components/home/Honoring.tsx
"use client";

import React, { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Button, Box, Grid } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { renderWithLineBreaks } from "@/app/utils/renderWithLineBreaks";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

export default function Honoring() {
  const t = useTranslations("Home");
  const locale = useLocale();

  const pagePath = "/"; // หน้า Home

  // ---- default text จาก i18n (ดีต่อ SEO / SSR) ----
  const defaultTitle = t("Honoring.title");
  const defaultSubtitle = t("Honoring.subtitle");
  const defaultContent1 = t("Honoring.content1");
  const defaultContent2 = t("Honoring.content2");

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = useState(defaultTitle);
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [content1, setContent1] = useState(defaultContent1);
  const [content2, setContent2] = useState(defaultContent2);

  // อ่านสถานะ expand (เดิม)
  const [isExpanded, setIsExpanded] = useState(false);

  // เมื่อ locale เปลี่ยน → reset กลับค่า default ก่อน
  useEffect(() => {
    setTitle(defaultTitle);
    setSubtitle(defaultSubtitle);
    setContent1(defaultContent1);
    setContent2(defaultContent2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    let cancelled = false;

    const fetchInline = async () => {
      try {
        const websiteId =
          process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID || "";

        const res = await fetch(
          `/api/admin/page-inline?websiteId=${encodeURIComponent(
            websiteId
          )}&path=${encodeURIComponent(pagePath)}&locale=${encodeURIComponent(
            String(locale)
          )}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;

        // map จาก cmsKey (ถ้ามี) หรือดึงจาก selector (กรณี row เก่า)
        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          if (!key) {
            const match = e.selector.match(/data-cms-key="([^"]+)"/);
            if (match) key = match[1];
          }

          if (key) map[key] = e.value;
        });

        if (cancelled) return;

        // override เฉพาะ key ที่มีใน inline
        if (map["home.honoring.title"]) setTitle(map["home.honoring.title"]);
        if (map["home.honoring.subtitle"])
          setSubtitle(map["home.honoring.subtitle"]);
        if (map["home.honoring.content1"])
          setContent1(map["home.honoring.content1"]);
        if (map["home.honoring.content2"])
          setContent2(map["home.honoring.content2"]);
      } catch (err) {
        console.warn("[Honoring] inline fetch error:", err);
      }
    };

    fetchInline();

    return () => {
      cancelled = true;
    };
  }, [locale, pagePath]);

  return (
    <Box className="honoring-wrap">
      <Grid container sx={{ display: { xs: "none", lg: "flex" } }}>
        <Grid size={5} offset={7} data-aos="fade-right">
          <Image
            src="/assets/home/honoring/honoring.png"
            width={637}
            height={725}
            title="poonphol honoring"
            alt="poonphol honoring"
          />
        </Grid>
      </Grid>
      <Box className="honoring-content" sx={{
        pt: { xs: 5, lg: 0 }
      }}>
        <Container className="container" data-aos="fade-up">
          <Grid container>
            <Grid size={{ xs: 12, lg: 7.5 }}>
              <Box
                className="honoring-content1"
                sx={{ display: isExpanded ? "none" : "block" }}
              >
                <Typography
                  variant="h2"
                  data-heading-tag="H2"
                  data-cms-key="home.honoring.title"
                  sx={{
                    mb: 2,
                    whiteSpace: { xs: "normal", lg: "pre-line" }
                  }}>
                  {renderWithLineBreaks(title)}
                </Typography>

                <Typography
                  variant="h3"
                  data-heading-tag="H3"
                  data-cms-key="home.honoring.subtitle"
                  sx={{
                    mb: 3
                  }}
                >
                  {renderWithLineBreaks(subtitle)}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{ mb: 3 }}
                  data-cms-key="home.honoring.content1"
                >
                  {renderWithLineBreaks(content1)}
                </Typography>
              </Box>

              <Box
                className="honoring-content2"
                sx={{
                  display: isExpanded ? "block" : "none",
                  whiteSpace: "pre-line",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ mb: 3 }}
                  data-cms-key="home.honoring.content2"
                >
                  {renderWithLineBreaks(content2)}
                </Typography>
              </Box>

              {!isExpanded ? (
                <Button
                  variant="contained"
                  className="readmore-btn"
                  onClick={() => setIsExpanded(true)}
                >
                  {t("viewMore")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  className="readmore-btn"
                  onClick={() => setIsExpanded(false)}
                >
                  {t("viewLess")}
                </Button>
              )}
            </Grid>
          </Grid>

          <Grid container sx={{ display: { xs: "flex", lg: "none" } }}>
            <Grid size={12} data-aos="fade-up">
              <Image
                src="/assets/home/honoring/honoring.png"
                width={637}
                height={725}
                title="poonphol honoring"
                alt="poonphol honoring"
                className="img-fluid"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
